import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Button, Input, Label } from '@alertwhale/ui'
import { useForgotPassword } from '@/hooks/useAuth'
import { Mail, Loader2, ArrowLeft, CheckCircle } from 'lucide-react'

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const forgotPassword = useForgotPassword()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await forgotPassword.mutateAsync({ email })
    setSubmitted(true)
  }

  if (submitted) {
    return (
      <div className="p-8">
        <div className="space-y-4 text-center mb-8">
          <div className="mx-auto w-12 h-12 bg-emerald-500/20 rounded-full flex items-center justify-center">
            <CheckCircle className="w-6 h-6 text-emerald-400" />
          </div>
          <h1 className="text-2xl font-bold text-white">Check your email</h1>
          <p className="text-slate-400">
            We've sent a password reset link to <strong className="text-white">{email}</strong>
          </p>
        </div>

        <div className="space-y-4">
          <p className="text-sm text-center text-slate-400">
            Didn't receive the email? Check your spam folder or{' '}
            <button
              onClick={() => setSubmitted(false)}
              className="text-whale-400 hover:text-whale-300"
            >
              try again
            </button>
          </p>
          <Link to="/login">
            <Button
              variant="outline"
              className="w-full border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to login
            </Button>
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="p-8">
      <div className="space-y-2 text-center mb-8">
        <h1 className="text-2xl font-bold text-white">Forgot password?</h1>
        <p className="text-slate-400">
          Enter your email address and we'll send you a link to reset your password
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="email" className="text-slate-300">Email</Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <Input
              id="email"
              type="email"
              placeholder="name@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="pl-10 bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-whale-500 focus:ring-whale-500/20"
              required
            />
          </div>
        </div>

        {forgotPassword.error && (
          <div className="text-sm text-red-400">
            {forgotPassword.error.message}
          </div>
        )}

        <Button
          type="submit"
          className="w-full bg-whale-500 hover:bg-whale-600 text-white font-medium"
          disabled={forgotPassword.isPending}
        >
          {forgotPassword.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Send reset link
        </Button>

        <Link to="/login">
          <Button
            variant="outline"
            className="w-full border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to login
          </Button>
        </Link>
      </form>
    </div>
  )
}
